import { PayloadAction } from '@reduxjs/toolkit';
import { isDefined } from '@yandex-int/messenger.utils';
import { call, put, select } from 'typed-redux-saga';

import { SELF } from '../../../../../shared/constants';
import { setDeviceErrorModalVisibility } from '../../../../../shared/features/device-manager/slice';
import { deviceManager } from '../../../../../shared/features/goloom-sdk';
import { hideNotification } from '../../../../../shared/features/notifications/ps-notifications';
import { selectJoinWithCameraOff, selectJoinWithMicrophoneOff } from '../../../../../shared/features/settings';
import { browser } from '../../../../../shared/helpers/browser-capabilities';
import { ACTION, FEATURE } from '../../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { getParticipant, getParticipantById, getSelfMutedState } from '../../../selectors';
import { NotificationId, ToggleMuteActionInterface } from '../../../types';
import { updateParticipant } from '../../conference/slice';
import { setAudioIsTemporarilyUnMuted, toggleMuteAction } from '../slice';

const logger = createLogger('goloom:mute');

/**
 * Сага ответственная за переключение мьютов аудио/видео
 */
export function* toggleMuteSaga({ payload }: PayloadAction<ToggleMuteActionInterface>) {
    const { type, value, isTemporarily, isOptimistic } = payload;
    const participant = yield* select(getParticipant, SELF);

    const muted = (() => {
        // Если вызвали сагу с определенным значением мьюта, то применяем его
        if (isDefined(value)) {
            return value;
        }

        // Если происходит тоггл значения, то пробуем смотреть на состояние в goloomSdk.session
        if (isDefined(deviceManager.mutedState[type])) {
            return !deviceManager.mutedState[type];
        }

        // Иначе смотрим на значение в store
        return !participant[`${type}TrackState`]?.muted;
    })();

    logger.event('Function toggleMuteSaga called', {
        additional: {
            method: 'toggleMuteSaga',
            type,
            payloadMuted: value,
            muted,
            isTemporarily,
        },
    });

    try {
        yield* put(
            hideNotification(
                type === 'video' ? NotificationId.CAMERA_ACCESS_ERROR : NotificationId.MICROPHONE_ACCESS_ERROR
            )
        );
        if (type === 'video') {
            yield* call([deviceManager, deviceManager.setVideoStatus], !muted, isOptimistic);

            logger.endFeatureDelta({
                feature: FEATURE.MUTE,
                action: muted ? ACTION.CAMERA_OFF : ACTION.CAMERA_ON,
                isSuccess: true,
            });
        }

        if (type === 'audio') {
            yield* call([deviceManager, deviceManager.setAudioStatus], !muted, isOptimistic);
            yield* put(setAudioIsTemporarilyUnMuted(isTemporarily ? !muted : false));

            logger.endFeatureDelta({
                feature: FEATURE.MUTE,
                action: muted ? ACTION.MIC_OFF : ACTION.MIC_ON,
                isSuccess: true,
            });
        }
    } catch (error) {
        /** Если поймали ошибку по девайсу, то показываем модалку */
        const kind = type === 'audio' ? 'audioinput' : 'videoinput';
        yield* put(setDeviceErrorModalVisibility({ kind, isVisible: true }));

        if (type === 'audio') {
            logger.endFeatureDelta({
                feature: FEATURE.MUTE,
                action: muted ? ACTION.MIC_OFF : ACTION.MIC_ON,
                isSuccess: false,
            });
        }

        if (type === 'video') {
            logger.endFeatureDelta({
                feature: FEATURE.MUTE,
                action: muted ? ACTION.CAMERA_OFF : ACTION.CAMERA_ON,
                isSuccess: false,
            });
        }

        // Реакция на ошибку происходит в deviceManager,
        // но так как мы пробрасываем ее оттуда дальше, то здесь просто ловим и останавливаем,
        // но обязательно логируем
        logger.warn(error, {
            method: 'toggleMuteSaga',
            additional: {
                type,
                payloadMuted: value,
                muted,
                isTemporarily,
            },
        });
    }
}

export function* initMuteState(options?: { forceCurrentState?: boolean }) {
    const { forceCurrentState = false } = options ?? {};

    const params = new URLSearchParams(document.location.search);
    const currentMutedState = yield* select(getSelfMutedState);

    const queryMicMuted = params.get('mic') === 'off';
    const queryCameraMuted = params.get('camera') === 'off';

    const joinWithMicrophoneOff = yield* select(selectJoinWithMicrophoneOff);
    const joinWithCameraOff = yield* select(selectJoinWithCameraOff);

    const desiredAudioMuted = forceCurrentState
        ? currentMutedState?.audioMuted
        : queryMicMuted || joinWithMicrophoneOff;
    const desiredVideoMuted = forceCurrentState ? currentMutedState?.videoMuted : queryCameraMuted || joinWithCameraOff;

    const audioPermission = yield* call([deviceManager, deviceManager.getAudioInputPermission]);
    const videoPermission = yield* call([deviceManager, deviceManager.getVideoInputPermission]);

    const isAudioMuted =
        !audioPermission || browser()?.isSafariBased() || audioPermission === 'granted' ? desiredAudioMuted : undefined;
    const isVideoMuted =
        !videoPermission || browser()?.isSafariBased() || videoPermission === 'granted' ? desiredVideoMuted : undefined;

    logger.event('Optimistic update of initial devices mute state', {
        additional: {
            method: 'initMuteState',
            isAudioMuted,
            isVideoMuted,
        },
    });

    // Делаем оптимистичное обновление чтобы моментально отобразить начальное состояние устройств: "включено", если есть
    // пермишены
    yield* put(
        updateParticipant({
            id: SELF,
            audio: {
                id: '',
                muted: isAudioMuted ?? true,
            },
            video: {
                id: '',
                muted: isVideoMuted ?? true,
            },
        })
    );

    yield* call(
        toggleMuteSaga,
        toggleMuteAction({
            type: 'audio',
            value: desiredAudioMuted,
            isOptimistic: true,
        })
    );

    yield* call(
        toggleMuteSaga,
        toggleMuteAction({
            type: 'video',
            value: desiredVideoMuted,
            isOptimistic: true,
        })
    );
}

export function* changeAudioStatusSaga(status: boolean) {
    const participant = yield* select(getParticipantById, SELF);
    const currentMuted = participant?.audio?.muted;
    const newMuted = !status;

    if (currentMuted === newMuted) {
        logger.event('The new value provided is the same as the current value. No change is required', {
            additional: {
                method: 'changeAudioStatusSaga',
                type: 'audio',
                status,
                currentMuted,
                newMuted,
            },
        });
        return;
    }

    logger.event('Applying new audio status', {
        additional: {
            method: 'changeAudioStatusSaga',
            type: 'audio',
            status,
            currentMuted,
            newMuted,
        },
    });
    yield* put(
        updateParticipant({
            id: SELF,
            audio: {
                id: '',
                muted: newMuted,
            },
        })
    );
}

export function* changeVideoStatusSaga(status: boolean) {
    const participant = yield* select(getParticipantById, SELF);
    const currentMuted = participant?.video?.muted;
    const newMuted = !status;

    if (currentMuted === newMuted) {
        logger.event('The new value provided is the same as the current value. No change is required', {
            additional: {
                method: 'changeVideoStatusSaga',
                type: 'video',
                status,
                currentMuted,
                newMuted,
            },
        });
        return;
    }

    logger.event('Applying new video status', {
        additional: {
            method: 'changeVideoStatusSaga',
            type: 'video',
            status,
            currentMuted,
            newMuted,
        },
    });
    yield* put(
        updateParticipant({
            id: SELF,
            video: {
                id: '',
                muted: newMuted,
            },
        })
    );
}
